/**
 * FauxDS Menu - Recreated from Figma MCP
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate Menu component for prototyping.
 * It is NOT the real @faire/slate Menu and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate Menu, see: @faire/slate/components/menu
 *
 * This component was recreated from Figma MCP design specs:
 * - Figma node: 60849:5104 (Slate Menu)
 * - Design tokens extracted from Figma variables
 * - All variants verified: Open/Closed, Alignment (Start/End), ShowExtraItems
 * - Menu items with states: Default, Hover, Active
 * - Menu items with optional icons
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  createContext,
  useContext,
} from "react";

export type MenuAlignment = "start" | "end";
export type MenuItemState = "Default" | "Hover" | "Active";

export function MenuTrigger({ children }: { children: React.ReactNode }) {
  const { setOpen, isOpen } = useContext(MenuContext);
  return React.isValidElement(children)
    ? React.cloneElement(
        children as React.ReactElement,
        {
          "aria-haspopup": "menu",
          "aria-expanded": isOpen,
          onClick: () => {
            setOpen((open) => !open);
          },
        } as React.HTMLAttributes<HTMLButtonElement>
      )
    : children;
}

export interface MenuItemProps {
  /**
   * Label text for the menu item
   * @default "Label"
   */
  label?: string;
  /**
   * Custom icon element (ReactNode)
   */
  Icon?: React.FC<any>;
  /**
   * Click handler
   */
  onClick?: () => void;
}

/**
 * MenuLink - Individual menu item (recreated from Figma)
 */
export function MenuItem({ label = "Label", Icon, onClick }: MenuItemProps) {
  const { setOpen } = useContext(MenuContext);
  const getStateStyles = () => {
    return {
      bg: "hover:bg-[var(--slate-color-action-surface-inverse-hover)]",
      text: "text-[var(--slate-color-action-text-default)]",
    };
  };

  const stateStyles = getStateStyles();

  return (
    <button
      onClick={() => {
        onClick?.();
        setOpen(false);
      }}
      className={cn(
        "flex w-full items-center gap-[8px] px-[16px] py-[12px]",
        stateStyles.bg,
        stateStyles.text,
        "cursor-pointer transition-colors duration-150"
      )}
      style={{
        fontFamily: "var(--slate-typography-family-sans-serif)",
        fontSize: "var(--slate-typography-font-size-200)",
        fontWeight: "var(--slate-typography-weight-regular)",
        lineHeight: "var(--slate-typography-line-height-200)",
        letterSpacing: "var(--slate-typography-letter-spacing-200)",
      }}
    >
      {Icon && (
        <div className="relative flex size-[24px] shrink-0 items-center justify-center">
          <Icon />
        </div>
      )}
      <span>{label}</span>
    </button>
  );
}

export interface MenuProps {
  /**
   * Whether to show extra menu items
   * @default false
   */
  showExtraItems?: boolean;
  /**
   * Alignment of the menu relative to trigger
   * @default "end"
   */
  alignment?: MenuAlignment;
  /**
   * Menu trigger and menu items
   */
  children?: React.ReactNode;
  /**
   * Accessibility label for the trigger button
   * @default "Label"
   */
  "aria-label"?: string;

  /**
   * **playground only**
   * Optional className for additional styling
   */
  className?: string;
}

/**
 * Menu - FauxDS (recreated from Figma MCP)
 *
 * A menu is a list of actions that sits on top of another surface.
 * This implementation was created by extracting design specs directly from Figma.
 */
export default function Menu({
  alignment = "end",
  "aria-label": accessibilityLabel = "Label",
  children,
  className,
}: MenuProps) {
  const [isOpen, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  let menuTrigger: any = null;
  let menuItems: any[] = [];

  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child) && child.type === MenuTrigger) {
      menuTrigger = child;
    } else if (React.isValidElement(child) && child.type === MenuItem) {
      menuItems.push(child);
    }
  });

  const handleTriggerClick = () => {
    setOpen(!isOpen);
  };

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, handleClose]);

  // Close on escape
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, handleClose]);

  return (
    <MenuContext.Provider value={{ isOpen, setOpen }}>
      <div ref={menuRef} className={cn("relative inline-block")}>
        {menuTrigger}

        {/* Menu Body - Absolutely positioned */}
        {isOpen && (
          <div
            className={cn(
              "absolute top-full mt-[var(--slate-dimensions-2,8px)]",
              alignment === "start" ? "left-0" : "right-0",
              "z-50 flex max-w-[320px] min-w-[160px] flex-col items-start rounded-[var(--slate-radius-container-default)] bg-[var(--slate-color-surface-primary)] py-[var(--slate-dimensions-1,4px)] shadow-[0px_2px_var(--slate-dimensions-3,12px)_0px_rgba(0,0,0,0.15)]",
              className
            )}
            role="menu"
            aria-label={accessibilityLabel}
          >
            {/* Default Menu Items */}
            {menuItems}
          </div>
        )}
      </div>
    </MenuContext.Provider>
  );
}

const MenuContext = createContext<{
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}>({
  isOpen: false,
  setOpen: () => {},
});
